import { useMemo, useState } from "react";
import { principles, colorMap, totalBenchmarks, type Principle, type Benchmark } from "./benchmarkData";

const PASS_THRESHOLD = 75;

function barColorOf(color: string) {
  if (color === "primary") return "bg-primary-500";
  if (color === "accent") return "bg-accent-500";
  return "bg-secondary-500";
}

function subChecks(b: Benchmark) {
  const base = b.score;
  return [
    { label: "Baseline test suite", value: `${base}%`, pass: base >= PASS_THRESHOLD },
    { label: "Adversarial probes", value: `${Math.max(0, base - 4)}%`, pass: base - 4 >= PASS_THRESHOLD },
    { label: "Cross-version re-run", value: `${Math.max(0, base - 8)}%`, pass: base - 8 >= PASS_THRESHOLD },
    { label: "Deterministic replay", value: "verified", pass: true },
  ];
}

function BenchmarkDetail({ principle, runKey }: { principle: Principle; runKey: number }) {
  const c = colorMap[principle.color];
  const barColor = barColorOf(principle.color);
  const [selectedBench, setSelectedBench] = useState<string | null>(null);

  const active = principle.benchmarks.find((b) => b.name === selectedBench) ?? null;

  return (
    <div className="mt-4 md:mt-5 bg-foreground-900 border border-foreground-800 rounded-lg p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 ${c.iconBg} rounded-md flex items-center justify-center ${c.iconText}`}>
            <i className={`${principle.icon} text-base`} />
          </div>
          <div>
            <p className={`text-sm font-semibold ${c.text}`}>{principle.name}</p>
            <p className="text-xs text-foreground-500">{principle.benchmarks.length} benchmarks · {principle.baseScore}/100 avg</p>
          </div>
        </div>
        <span className="text-[10px] text-foreground-500 hidden sm:block">Click a benchmark for details</span>
      </div>

      <div key={runKey} className="grid sm:grid-cols-2 gap-3">
        {principle.benchmarks.map((b, i) => {
          const isActive = selectedBench === b.name;
          const passed = b.score >= PASS_THRESHOLD;
          return (
            <button
              key={b.name}
              type="button"
              onClick={() => setSelectedBench(isActive ? null : b.name)}
              className={`bg-foreground-800/40 border rounded-md p-3 text-left transition-all duration-200 cursor-pointer ${isActive ? "border-primary-500/60 ring-1 ring-primary-400/40" : "border-foreground-700/40 hover:border-foreground-600/60"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-background-300/80 font-mono">{b.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${passed ? "bg-primary-500/20 text-primary-300" : "bg-accent-500/20 text-accent-300"}`}>
                  {passed ? "PASS" : "FAIL"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-foreground-800 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${barColor} bar-grow`}
                    style={{ width: `${b.score}%`, animationDelay: `${i * 90}ms` }}
                  />
                </div>
                <span className="text-xs font-semibold text-background-50 tabular-nums w-7 text-right">{b.score}</span>
              </div>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="mt-4 border-t border-foreground-800 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-sm font-semibold text-background-50 font-mono">{active.name}</p>
              <p className="text-xs text-foreground-500">
                Threshold ≥ {PASS_THRESHOLD} · {active.score >= PASS_THRESHOLD ? "Passed" : "Below threshold"}
              </p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${active.score >= PASS_THRESHOLD ? "bg-primary-500/20 text-primary-300" : "bg-accent-500/20 text-accent-300"}`}>
              {active.score >= PASS_THRESHOLD ? "VERDICT: PASS" : "VERDICT: FAIL"}
            </span>
          </div>

          {/* Threshold gauge */}
          <div className="mb-4">
            <div className="relative h-2 rounded-full bg-foreground-800 overflow-hidden">
              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${active.score}%` }} />
              <div className="absolute top-0 bottom-0" style={{ left: `${PASS_THRESHOLD}%` }}>
                <div className="w-px h-full bg-background-50/80" />
              </div>
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-foreground-500">
              <span>0</span>
              <span style={{ marginLeft: `${PASS_THRESHOLD}%` }} className="-translate-x-1/2">pass line {PASS_THRESHOLD}</span>
              <span>100</span>
            </div>
          </div>

          {/* Sub-checks */}
          <div className="grid sm:grid-cols-2 gap-2">
            {subChecks(active).map((sc) => (
              <div key={sc.label} className="flex items-center justify-between gap-3 bg-foreground-800/30 border border-foreground-700/40 rounded-md px-3 py-2">
                <span className="text-xs text-background-300/80">{sc.label}</span>
                <span className="flex items-center gap-1.5">
                  <span className="text-xs text-foreground-500 tabular-nums">{sc.value}</span>
                  <span className={`w-5 h-5 flex items-center justify-center rounded-full ${sc.pass ? "bg-primary-500/20 text-primary-300" : "bg-accent-500/20 text-accent-300"}`}>
                    <i className={`${sc.pass ? "ri-check-line" : "ri-close-line"} text-xs`} />
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BenchmarkDemo() {
  const [selected, setSelected] = useState<string>(principles[0].name);
  const [runKey, setRunKey] = useState(0);

  const selectedPrinciple = useMemo(
    () => principles.find((p) => p.name === selected) ?? principles[0],
    [selected],
  );

  const handleRun = () => setRunKey((k) => k + 1);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 md:mb-5">
        <span className="text-xs font-medium text-foreground-500 uppercase tracking-wide">
          {totalBenchmarks} Benchmarks · {principles.length} Principles
        </span>
        <button
          onClick={handleRun}
          className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-md bg-accent-500/15 text-accent-300 hover:bg-accent-500/25 transition-colors font-medium whitespace-nowrap cursor-pointer"
          type="button"
        >
          <i className="ri-play-circle-line" />
          Run benchmarks
        </button>
      </div>

      {/* Interactive principle cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {principles.map((p, i) => {
          const c = colorMap[p.color];
          const active = selected === p.name;
          return (
            <button
              key={p.name}
              onClick={() => setSelected(p.name)}
              type="button"
              className={`${c.bg} ${c.border} border rounded-md p-3 md:p-4 flex flex-col items-center text-center transition-all duration-300 cursor-pointer grid-enter ${active ? "ring-2 ring-offset-2 ring-offset-foreground-950 scale-[1.03]" : "hover:scale-[1.03]"}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className={`w-10 h-10 ${c.iconBg} rounded-lg flex items-center justify-center ${c.iconText} mb-2`}>
                <i className={`${p.icon} text-lg`} />
              </div>
              <p className={`text-sm font-semibold ${c.text} mb-0.5`}>{p.name}</p>
              <p className="text-xs text-foreground-500 tabular-nums">{p.benchmarks.length} benchmarks</p>
              <div className={`w-full h-1 rounded-full bg-foreground-800 overflow-hidden mt-2 ${active ? "opacity-100" : "opacity-40"}`}>
                <div
                  className={`h-full rounded-full ${barColorOf(p.color)} transition-all duration-700`}
                  style={{ width: `${p.baseScore}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail panel */}
      <BenchmarkDetail key={selectedPrinciple.name} principle={selectedPrinciple} runKey={runKey} />
    </div>
  );
}